import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Star, Pencil, Code, Eye } from 'lucide-react'
import { toast } from 'sonner'
import { billingTemplatesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { TemplateType } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

function BillingTemplatesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [selectedType, setSelectedType] = useState<TemplateType | 'ALL'>('ALL')

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['billingTemplates', selectedType],
    queryFn: () => billingTemplatesApi.getAll(selectedType === 'ALL' ? undefined : selectedType),
  })

  const deleteMutation = useMutation({
    mutationFn: billingTemplatesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingTemplates'] })
      toast.success('Template deleted successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete template'
      toast.error(errorMessage)
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: billingTemplatesApi.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingTemplates'] })
      toast.success('Default template updated')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to set default template'
      toast.error(errorMessage)
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Billing Templates</h1>
          <p className="text-muted-foreground">
            Configure templates for invoices and quotes with branding and default settings
          </p>
        </div>
        {canAddEdit('BILLING_TEMPLATES') && (
          <Button onClick={() => navigate('/settings/billing-templates/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        )}
      </div>

      <Tabs value={selectedType} onValueChange={(v) => setSelectedType(v as TemplateType | 'ALL')}>
        <TabsList>
          <TabsTrigger value="ALL">All</TabsTrigger>
          <TabsTrigger value="INVOICE">Invoice</TabsTrigger>
          <TabsTrigger value="QUOTE">Quote</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No billing templates configured. Click "Add Template" to create one.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Colors</TableHead>
                <TableHead>Font</TableHead>
                <TableHead>HTML</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.template_id}>
                  <TableCell className="font-medium">{template.template_name}</TableCell>
                  <TableCell>
                    <Badge variant={template.template_type === 'INVOICE' ? 'default' : 'secondary'}>
                      {template.template_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: template.primary_color }}
                        title={`Primary: ${template.primary_color}`}
                      />
                      <div
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: template.secondary_color }}
                        title={`Secondary: ${template.secondary_color}`}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{template.font_family}</TableCell>
                  <TableCell>
                    {template.html_template ? (
                      <Badge variant="secondary">
                        <Code className="h-3 w-3 mr-1" />
                        Custom
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Default</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {template.is_default ? (
                      <Badge variant="default">Default</Badge>
                    ) : canAddEdit('BILLING_TEMPLATES') ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDefaultMutation.mutate(template.template_id)}
                        disabled={setDefaultMutation.isPending}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Set Default
                      </Button>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/settings/billing-templates/${template.template_id}`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canAddEdit('BILLING_TEMPLATES') && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/settings/billing-templates/${template.template_id}/edit`)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteMutation.mutate(template.template_id)}
                            disabled={deleteMutation.isPending || template.is_default}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}

// Export with both names for backward compatibility
export { BillingTemplatesPage as default }

